import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import {
  Identifiable,
  ProjectStrategicAxis,
  StrategicAxis,
  WeatherReports,
} from "@planzone/dto";
import { UseCases } from "@planzone/core";
import {
  useAuthentication,
  useRetrieveCurrentPlanzone,
  useRetrieveStrategicAxes,
  useRetrieveProjectSynthesis,
  useRetrieveProjectStrategicAxes,
  useProjectEdition,
} from "@planzone/view-models";
import { RestrictedArea } from "@scenes/restrictedArea";
import { Safeguard } from "@scenes/safeguard";
import { WeightTag } from "@common/weightTag";
import { useModal } from "@root/client/modal";
import { WeatherReport } from "./weatherReport";
import { Section } from "./section";
import { TaskSection } from "./taskSection";
import { StrategicAxisCode } from "./strategicAxisTag";
import { Tasks } from "./tasks";
import { TasksLoader } from "./tasksLoader";
import { SubmitReportButton } from "./submitReportButton";
import { PdfExport } from "./pdfExport";
import { Reports } from "./reports";
import { Milestones } from "./milestones";
import { Monitoring } from "./monitoring";
import { MonitoringLoader } from "./monitoringLoader";
import { StrategicAxesIcon } from "./strategicAxesIcon";
import { EditIcon } from "./editIcon";
import { RichTextInput } from "@common/richTextInput";
import { Props } from "./types";
import styles from "./styles.scss";
import { ProjectStatus } from "@scenes/project/projectStatus";

function SynthesisPage({ project }: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const { account } = useAuthentication();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { isRequestPending: isEditRequestPending, editProject } =
    useProjectEdition(project);
  const { strategicAxes, retrieveStrategicAxes } = useRetrieveStrategicAxes();
  const [isEveryoneDisplayed, setIsEveryoneDisplayed] = useState(true);
  const [areEveryOverDueTaskDisplayed, setAreEveryOverDueTaskDisplayed] =
    useState(true);
  const {
    isRequestPending,
    isRequestSuccess,
    synthesis,
    retrieveProjectSynthesis,
  } = useRetrieveProjectSynthesis(project);
  const { totalWeight, projectStrategicAxes, retrieveProjectStrategicAxes } =
    useRetrieveProjectStrategicAxes(project);
  const canExportPdf = !project.isTemplate && isRequestSuccess;
  const filteredScores = (
    projectStrategicAxes
      .map(({ strategicAxisId, ...rest }) => ({
        ...rest,
        strategicAxe: strategicAxes.find(
          ({ uuid }) => uuid === strategicAxisId,
        ),
      }))
      .filter((s) => !!s) as (Identifiable<ProjectStrategicAxis> & {
      strategicAxe: Identifiable<StrategicAxis>;
    })[]
  )
    .filter(({ consistencyValue }) => consistencyValue > 0)
    .sort((current, next) =>
      current.consistencyValue * current.strategicAxe.weight >
      next.weight * next.strategicAxe.weight
        ? -1
        : 1,
    );

  React.useEffect(() => {
    !project.isTemplate &&
      !!project.uuid &&
      retrieveProjectSynthesis({ account, project });
    retrieveStrategicAxes(planzone);
    retrieveProjectStrategicAxes(project);
  }, [project.uuid]);

  return (
    <RestrictedArea areas={["strategicAxes"]}>
      {({ canAccessStrategicAxes }) => (
        <Safeguard
          useCases={[UseCases.submitProjectReport, UseCases.editProject]}
        >
          {({ canSubmitProjectReport, canEditProject }) => (
            <div
              className={classNames(styles.synthesisPage, {
                [styles.projectModel]: project.isTemplate,
              })}
            >
              <div className={styles.header}>
                {canAccessStrategicAxes && (
                  <div className={styles.strategicScore}>
                    <StrategicAxesIcon className={styles.icon} />
                    <div>{t("projectPage.synthesisPage.strategicScore")}:</div>
                    <WeightTag
                      weight={totalWeight}
                      maxWeight={strategicAxes.reduce(
                        (acc, { weight }) => acc + weight * 2,
                        0,
                      )}
                    />
                    {filteredScores.map(({ strategicAxe }) => (
                      <StrategicAxisCode {...strategicAxe} />
                    ))}
                    {filteredScores.length > 2 && (
                      <>+ {filteredScores.length - 2} ...</>
                    )}
                    <Safeguard
                      useCases={[
                        UseCases.createProjectStrategicAxis,
                        UseCases.editProjectStrategicAxis,
                      ]}
                    >
                      {({
                        canCreateProjectStrategicAxis,
                        canEditProjectStrategicAxis,
                      }) =>
                        canCreateProjectStrategicAxis &&
                        canEditProjectStrategicAxis && (
                          <div
                            className={styles.editProjectStrategicAxisIconBox}
                            onClick={() =>
                              pushView({
                                key: UseCases.createProjectStrategicAxis,
                                data: project,
                              })
                            }
                          >
                            <EditIcon
                              className={styles.editProjectStrategicAxisIcon}
                            />
                          </div>
                        )
                      }
                    </Safeguard>
                  </div>
                )}
                <div className={styles.weatherReport}>
                  <WeatherReport
                    project={project}
                    weatherReport={project.attributes.weather as WeatherReports}
                  />
                  <ProjectStatus
                    project={project}
                    selectStatus={(status) =>
                      !isRequestPending &&
                      editProject({
                        project,
                        nextProject: { ...project, status },
                      })
                    }
                  />
                  {canExportPdf && canEditProject && (
                    <PdfExport project={project} synthesis={synthesis} />
                  )}
                  {canSubmitProjectReport && (
                    <SubmitReportButton
                      submitReport={() =>
                        pushView({
                          key: UseCases.submitProjectReport,
                          data: { project },
                        })
                      }
                    />
                  )}
                </div>
              </div>
              <div className={styles.main}>
                <div className={styles.row}>
                  <div className={styles.reports}>
                    <Section
                      title={t("projectPage.synthesisPage.latestReports")}
                    >
                      <Reports project={project} />
                    </Section>
                  </div>
                  <div className={styles.descriptionSection}>
                    <Section
                      title={t("projectPage.synthesisPage.description")}
                      edit={
                        canEditProject
                          ? () =>
                              pushView({
                                key: UseCases.retrieveProject,
                                data: { project, autoFocusDescription: true },
                              })
                          : undefined
                      }
                    >
                      <div className={styles.description}>
                        <RichTextInput
                          value={project.description}
                          isDisabled
                          input={() => {}}
                        />
                      </div>
                    </Section>
                  </div>
                </div>
                <div className={styles.monitoring}>
                  <div className={styles.milestonesSection}>
                    <Section
                      title={t("projectPage.synthesisPage.milestones.title")}
                    >
                      <div className={styles.milestones}>
                        {!isRequestPending ? (
                          <Milestones
                            milestones={[
                              ...synthesis.delayedMilestones,
                              ...synthesis.upComingMilestones,
                              ...synthesis.finishedMilestones,
                            ]}
                            placeholder={t(
                              "projectPage.synthesisPage.milestones.placeholder",
                            )}
                          />
                        ) : (
                          <TasksLoader />
                        )}
                      </div>
                    </Section>
                  </div>
                  <div className={styles.monitoringSection}>
                    <Section
                      title={t("projectPage.synthesisPage.monitoring.title")}
                    >
                      <div className={styles.monitoringMain}>
                        {!isRequestPending ? (
                          <Monitoring synthesis={synthesis} project={project} />
                        ) : (
                          <MonitoringLoader />
                        )}
                      </div>
                    </Section>
                  </div>
                </div>
                <div className={styles.tasks}>
                  <div className={styles.upComingTasks}>
                    <TaskSection
                      title={
                        <div className={styles.upComingTasksTitle}>
                          {t("projectPage.synthesisPage.upComingTasks.title")}
                          <p className={styles.upComingTasksNumber}>
                            {synthesis.upComingTasks.length}
                          </p>
                        </div>
                      }
                      displayOnlyUser={isEveryoneDisplayed}
                      setDisplayOnlyUser={setIsEveryoneDisplayed}
                    >
                      <div className={styles.taskMain}>
                        {!isRequestPending ? (
                          <Tasks
                            tasks={synthesis.upComingTasks}
                            placeholder={
                              isEveryoneDisplayed
                                ? t(
                                    "projectPage.synthesisPage.upComingTasks.placeholder",
                                  )
                                : t(
                                    "projectPage.synthesisPage.upComingTasks.myPlaceholder",
                                  )
                            }
                            isEveryoneDisplayed={isEveryoneDisplayed}
                          />
                        ) : (
                          <TasksLoader />
                        )}
                      </div>
                    </TaskSection>
                  </div>
                  <div className={styles.overDueTasks}>
                    <TaskSection
                      title={
                        <div className={styles.overDueTasksTitle}>
                          {t("projectPage.synthesisPage.overDueTasks.title")}
                          <p className={styles.overDueTasksNumber}>
                            {synthesis.delayedTasks.length}
                          </p>
                        </div>
                      }
                      displayOnlyUser={areEveryOverDueTaskDisplayed}
                      setDisplayOnlyUser={setAreEveryOverDueTaskDisplayed}
                    >
                      <div className={styles.taskMain}>
                        {!isRequestPending ? (
                          <Tasks
                            tasks={synthesis.delayedTasks}
                            placeholder={
                              areEveryOverDueTaskDisplayed
                                ? t(
                                    "projectPage.synthesisPage.overDueTasks.placeholder",
                                  )
                                : t(
                                    "projectPage.synthesisPage.overDueTasks.myPlaceholder",
                                  )
                            }
                            isEveryoneDisplayed={areEveryOverDueTaskDisplayed}
                          />
                        ) : (
                          <TasksLoader />
                        )}
                      </div>
                    </TaskSection>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Safeguard>
      )}
    </RestrictedArea>
  );
}

export { SynthesisPage };
